import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { datadogLogs } from '@datadog/browser-logs';

import { colors } from 'shared/themes';
import useErnie from 'shared/hooks/use-ernie';
import { useStores } from 'src/hooks/use-stores';
import devAccountsQuery from 'shared/graphql/user/queries/dev-accounts.gql';
import devLoginAdminMutation from 'shared/graphql/user/mutations/dev-login-admin.gql';
import type { GqlDevAccountsQuery, GqlDevLoginAdminMutation, GqlDevLoginAdminMutationVariables } from 'types/graphql';

type DevAccount = GqlDevAccountsQuery['devAccounts'][number];

// Account types that can sign into the admin app. Consumer ("user") accounts
// are excluded — they belong to the consumer marketplace picker.
const ADMIN_ACCOUNT_TYPES = ['dispensaryUser', 'superAdmin'];

type DevAccountPickerProps = {
  onSuccess: () => void;
};

/**
 * Dev-only one-click sign in for the accounts seeded by Armageddon's
 * DataMigrators::DevAccountsSeed. Renders nothing unless the backend reports
 * seeded admin accounts (i.e. DevLogin is enabled), so it is inert in any
 * environment where the dev picker isn't turned on. Gated by the parent via
 * shouldShowDevAccountPicker() — requires the explicit ENABLE_DEV_ACCOUNT_PICKER
 * opt-in env var, with appEnv !== 'production' as a second, independent guard.
 *
 * Uses the MobX-held ApolloClient directly (rather than generated
 * @apollo/react-hooks hooks) because the login route renders outside that
 * package's provider context — the same client the real admin login uses.
 */
export function DevAccountPicker({ onSuccess }: DevAccountPickerProps): JSX.Element | null {
  const { User, apolloClient } = useStores();
  const showErnie = useErnie();
  const [accounts, setAccounts] = useState<DevAccount[]>([]);
  const [signingIn, setSigningIn] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState('');

  useEffect(() => {
    let active = true;

    apolloClient
      .query<GqlDevAccountsQuery>({ query: devAccountsQuery, fetchPolicy: 'network-only' })
      .then((result) => {
        if (!active) {
          return;
        }
        const adminAccounts = result.data.devAccounts.filter((account) =>
          ADMIN_ACCOUNT_TYPES.includes(account.type ?? '')
        );
        setAccounts(adminAccounts);
      })
      .catch(() => {
        // DevLogin disabled (or backend unavailable): leave the picker hidden.
      });

    return () => {
      active = false;
    };
  }, [apolloClient]);

  if (accounts.length === 0) {
    return null;
  }

  const handleChange = async (event: React.ChangeEvent<HTMLSelectElement>): Promise<void> => {
    const accountId = event.target.value;
    if (!accountId) {
      return;
    }

    setSelectedAccountId(accountId);
    setSigningIn(true);
    try {
      const response = await apolloClient.mutate<GqlDevLoginAdminMutation, GqlDevLoginAdminMutationVariables>({
        mutation: devLoginAdminMutation,
        variables: { accountId },
      });
      const accessToken = response.data?.devLoginAdmin?.accessToken;
      if (!accessToken) {
        throw new Error('No access token returned from devLoginAdmin');
      }

      User.handleSetupAuthorizationToken(accessToken);
      await User.fetchUser();
      onSuccess();
    } catch (error) {
      showErnie('Could not sign in as the selected dev account.', 'danger');
      datadogLogs.logger.error('Dev account login error', { pageName: 'login', error });
      setSigningIn(false);
      // Reset so re-selecting the same (failed) account fires onChange again —
      // a native <select> won't re-fire for a value that's already selected.
      setSelectedAccountId('');
    }
  };

  return (
    <Wrapper data-testid='dev-account-picker'>
      <Label htmlFor='dev-account-select'>Dev sign in</Label>
      <Select
        id='dev-account-select'
        data-testid='dev-account-select'
        value={selectedAccountId}
        disabled={signingIn}
        onChange={(e) => void handleChange(e)}
      >
        <option value='' disabled>
          {signingIn ? 'Signing in…' : 'Assume a seeded account…'}
        </option>
        {accounts.map((account) => (
          <option key={account.id} value={account.id}>
            {account.label} ({account.type})
          </option>
        ))}
      </Select>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  border: 1px dashed ${colors.grey[75]};
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 20px;
  padding: 12px;
`;

const Label = styled.label`
  color: ${colors.grey[45]};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
`;

const Select = styled.select`
  border: 1px solid ${colors.grey[75]};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  padding: 8px;
  width: 100%;
`;
